import React, { useState, useEffect, useMemo, useCallback } from "react";
import { api } from "../api/client";
import { requestAndSubscribePush } from "../hooks/usePushNotifications";
import useGoogleCalendarEvents from "../hooks/useGoogleCalendarEvents";
import useICalEvents from "../hooks/useICalEventsNew";
import { useOutlookEvents } from "../hooks/useOutlookEvents";
import { formatNoteDate } from "../utils/dateUtils";
import { priorityRank } from "../utils/constants";
import { isGoogleCalendarTask, isICalTask, isOutlookTask } from "../utils/calendarUtils";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import MorningBriefing from "./Briefing/MorningBriefing";
import OnboardingChecklist from "./OnboardingChecklist";
import FollowUpEngine from "./FollowUp/FollowUpEngine";
import FollowUpPanel from "./FollowUp/FollowUpPanel";
import PulseWidget from "./Pulse/PulseWidget";
import LeaveRequests from "./LeaveRequests";
import HelpButton from '../tours/HelpButton';
import useAutoTour from '../tours/useAutoTour';


const PRESET_COLOURS = ['#b0bec5', '#E4E69B', '#4fc3f7', '#81c784', '#ef9a9a', '#ce93d8', '#ffcc80', '#80cbc4'];

// ── Family Chat dashboard widget ──────────────────────────────────────────────
function FamilyChatWidget({ onOpen }) {
  const { currentUser } = useCurrentUser();
  const [messages,       setMessages]       = useState([]);
  const [participantCount, setParticipantCount] = useState(0);
  const [reply,          setReply]          = useState('');
  const [sending,        setSending]        = useState(false);
  const [ready,          setReady]          = useState(false);

  const loadMessages = useCallback(() => {
    api('/api/family-chat')
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setMessages(d.messages || []);
        setParticipantCount(d.participantCount ?? 0);
        setReady(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadMessages();
    const t = setInterval(loadMessages, 20000);
    return () => clearInterval(t);
  }, [loadMessages]);

  if (!ready || participantCount < 2) return null;

  const preview = messages.slice(-5);

  async function handleSend(e) {
    e.preventDefault();
    const body = reply.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await api('/api/family-chat', { method: 'POST', body: JSON.stringify({ body }) });
      setReply('');
      loadMessages();
    } catch { /* best-effort */ }
    finally  { setSending(false); }
  }

  function formatTs(iso) {
    const d = new Date(iso);
    const now = new Date();
    const diffMins = Math.floor((now - d) / 60000);
    if (diffMins < 1)  return 'just now';
    if (diffMins < 60) return `${diffMins}m`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }

  return (
    <div className="dashboard-box" style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0 }}>💬 Family Chat</h3>
        <button
          onClick={onOpen}
          style={{ padding: '0.25rem 0.7rem', background: '#E4E69B', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: '0.78rem' }}
        >
          Open →
        </button>
      </div>

      {preview.length === 0 ? (
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#aaa' }}>No messages yet — say hello!</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.75rem' }}>
          {preview.map(msg => {
            const isMe = msg.senderUserId === currentUser?.id;
            return (
              <div key={msg.id} style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', fontSize: '0.88rem' }}>
                <span style={{ fontWeight: 700, color: isMe ? '#0C2D48' : '#555', flexShrink: 0 }}>
                  {isMe ? 'You' : msg.senderName}
                </span>
                <span style={{ color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {msg.body}
                </span>
                <span style={{ fontSize: '0.72rem', color: '#bbb', flexShrink: 0 }}>{formatTs(msg.createdAt)}</span>
              </div>
            );
          })}
        </div>
      )}

      <form onSubmit={handleSend} style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          value={reply}
          onChange={e => setReply(e.target.value)}
          placeholder="Quick reply…"
          disabled={sending}
          style={{
            flex: 1, padding: '0.5rem 0.75rem',
            border: '1px solid #ccc', borderRadius: 8,
            fontSize: '0.9rem', outline: 'none',
          }}
        />
        <button
          type="submit"
          disabled={!reply.trim() || sending}
          style={{
            padding: '0.5rem 1rem',
            background: reply.trim() && !sending ? '#0C2D48' : '#dee2e6',
            color: reply.trim() && !sending ? '#E4E69B' : '#888',
            border: 'none', borderRadius: 8,
            fontWeight: 700, fontSize: '0.88rem',
            cursor: reply.trim() && !sending ? 'pointer' : 'not-allowed',
          }}
        >
          Send
        </button>
      </form>
    </div>
  );
}

function Dashboard({
  today,
  dashboardSortBy,
  setDashboardSortBy,
  getTodayTasks,
  setViewTask,
  taskTypes,
  quickNotes,
  newNote,
  setNewNote,
  addNote,
  deleteNote,
  renderProductivityView,
  setFilter,
  tasks,
  handleAddTaskFromChat,
  handleAideyEditTask,
  handleAideyDeleteTask,
  setViewNote,
  clashInfo,
  noteCategories = [],
  setNoteCategories,
  markComplete,
}) {
  const isMobileLayout = typeof window !== 'undefined'
    && window.matchMedia('(max-width: 767px)').matches;

  // Get current user context
  const { currentUser, notifications, dismissNotification, activeWorkspaceId, workspaces, isTeamAdmin } = useCurrentUser();
  const activeWorkspace = (workspaces || []).find(w => w.id === activeWorkspaceId) || null;
  const isPersonalWorkspace = activeWorkspace?.type === 'personal';

  // Filter notifications to the active workspace so business-workspace notifications
  // (leave requests, invoice alerts) do not bleed into the personal dashboard.
  // Workspace scoping is now enforced server-side (GET /api/notifications filters
  // by the active workspace + globals), so only the priority/state filter remains.
  const normalNotifications = (notifications || []).filter(n =>
    n.priority === 'normal' && !n.dismissed && !n.read
  );
  useAutoTour('dashboard');

  // PWA push prompt — check in useEffect only, never in useState initialiser
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'default') return;
    if (!currentUser) return;
    if (currentUser?.preferences?.notifications?.promptDismissed) return;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) setShowPushPrompt(true);
  }, [currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handlePushEnable() {
    setShowPushPrompt(false);
    await requestAndSubscribePush();
  }

  async function handlePushDismiss() {
    setShowPushPrompt(false);
    try {
      const prefs = currentUser?.preferences || {};
      await api('/api/users/me', {
        method: 'PUT',
        body: JSON.stringify({ preferences: { ...prefs, notifications: { ...(prefs.notifications || {}), promptDismissed: true } } }),
      });
    } catch { /* dismiss is best-effort */ }
  }

  const [users, setUsers] = useState([]);

  // Plan access check — team features require Team/Enterprise plan or active trial
  const userPlan = (currentUser?.plan || '').toLowerCase();
  const hasTeamAccess = userPlan === 'team' || userPlan === 'enterprise'
    || currentUser?.subscriptionStatus === 'trialing';

  // Team Chat — persisted to backend API
  const [activeTab, setActiveTab] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");

  const fetchMessages = useCallback(() => {
    if (!hasTeamAccess || !currentUser) return;
    api('/api/team-chat')
      .then(r => r.json())
      .then(d => { if (Array.isArray(d.messages)) setMessages(d.messages); })
      .catch(() => {});
  }, [hasTeamAccess, currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load real team members from the backend API (not the demo localStorage users)
  useEffect(() => {
    if (!hasTeamAccess || !currentUser) return;

    api('/api/team/members')
      .then(res => res.json())
      .then(data => {
        const members = (data.members || []).filter(m => m.id !== currentUser.id);
        setUsers(members);
        if (members.length > 0 && !activeTab) setActiveTab("all");
      })
      .catch(() => setUsers([]));

  }, [currentUser?.id, hasTeamAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch messages and poll every 30 s
  useEffect(() => {
    if (!hasTeamAccess || !currentUser) return;
    fetchMessages();
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  // Notes category state
  const [newNoteCategoryId, setNewNoteCategoryId] = useState('');
  const [showDashNewCat, setShowDashNewCat] = useState(false);
  const [dashNewCatName, setDashNewCatName] = useState('');
  const [dashNewCatColour, setDashNewCatColour] = useState(PRESET_COLOURS[0]);

  const createDashCategory = useCallback(async () => {
    if (!dashNewCatName.trim()) return;
    try {
      const res = await api('/api/note-categories', {
        method: 'POST',
        body: JSON.stringify({ name: dashNewCatName.trim(), colour: dashNewCatColour }),
      });
      const data = await res.json();
      if (data.category) {
        setNoteCategories && setNoteCategories(prev => [...prev, data.category]);
        setNewNoteCategoryId(data.category.id);
      }
    } catch { }
    setShowDashNewCat(false);
    setDashNewCatName('');
    setDashNewCatColour(PRESET_COLOURS[0]);
  }, [dashNewCatName, dashNewCatColour, setNoteCategories]);

  // Scroll to top state and functionality
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Get Google Calendar events
  const { events: googleEvents } = useGoogleCalendarEvents();

  // Get iCal events from user profile
  let userProfile = {};
  try { userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}"); } catch { /* use empty default */ }
  const icalUrl = userProfile.integrations?.ical?.connected ? userProfile.integrations.ical.url : null;
  const { events: icalEvents } = useICalEvents(icalUrl);

  // Get Outlook events
  const { events: outlookEvents } = useOutlookEvents();

  // ✅ USER-SPECIFIC TASK FILTERING - Dashboard shows ONLY current user's tasks
  const filterUserSpecificTasks = (tasks) => {
    if (!currentUser) return [];
    return tasks.filter(task => {
      if (!isGoogleCalendarTask(task) && !isICalTask(task) && !isOutlookTask(task)) {
        const isAssignedToUser = task.assignedTeam && task.assignedTeam.includes(currentUser.id);
        const isUserTask = task.userId === currentUser.id || task.createdBy === currentUser.id;
        // Fallback: legacy tasks with no user association show for user ID 1
        const isLegacyTask = !task.userId && !task.createdBy && (!task.assignedTeam || task.assignedTeam.length === 0);
        return isAssignedToUser || isUserTask || (isLegacyTask && currentUser.id === 1);
      }
      return true;
    });
  };

  // ✅ ORIGINAL TOP TASKS LOGIC - More efficient and focused
  const getTopFiveTasks = () => {
    const now = new Date();
    // Get only Aidey tasks (not external calendar events for top tasks)
    const aideyTasks = getTodayTasks();

    // Step 1: Filter for tasks due today (and not completed)
    const relevantTasks = aideyTasks.filter((task) => {
      if (task.completed) return false;
      const taskDate = new Date(task.datetime);
      return taskDate.toDateString() === now.toDateString();
    });

    // Apply user filtering
    const userFilteredTasks = filterUserSpecificTasks(relevantTasks);

    // Step 2: Sort by priority first, then by start time
    const sorted = [...userFilteredTasks].sort((a, b) => {
      const aTime = new Date(a.datetime);
      const bTime = new Date(b.datetime);

      const isAOverdue = aTime < now;
      const isBOverdue = bTime < now;

      // Overdue tasks come first
      if (isAOverdue !== isBOverdue) {
        return isAOverdue ? -1 : 1;
      }

      // Then sort by priority
      const priorityOrder = priorityRank[a.priority] - priorityRank[b.priority];
      if (priorityOrder !== 0) return priorityOrder;

      // Then by start time
      return aTime - bTime;
    });

    return sorted.slice(0, 5); // Limit to top 5
  };

  // ✅ DASHBOARD SORT FUNCTION
  const sortDashboardTasks = (tasks) => {
    return dashboardSortBy === "time"
      ? [...tasks].sort((a, b) => {
        // Handle tasks without time
        if (!a.time && !b.time) return 0;
        if (!a.time) return 1;
        if (!b.time) return -1;
        return a.time.localeCompare(b.time);
      })
      : [...tasks].sort((a, b) => {
        const priorityOrder = priorityRank[a.priority] - priorityRank[b.priority];
        if (priorityOrder !== 0) return priorityOrder;
        // Sort by time as secondary
        if (!a.time && !b.time) return 0;
        if (!a.time) return 1;
        if (!b.time) return -1;
        return a.time.localeCompare(b.time);
      });
  };

  // ✅ USE ORIGINAL TOP TASKS APPROACH
  const allTopTasks = getTopFiveTasks(); // up to 5 tasks
  const sortedTopTasks = sortDashboardTasks(allTopTasks);
  const topTasks = sortedTopTasks.slice(0, 5); // show top 5


  // Handle sending chat messages
  const handleSend = () => {
    if (!newMessage.trim()) return;
    const body = newMessage.trim();
    setNewMessage('');
    const optimistic = { id: `opt-${Date.now()}`, user_id: currentUser.id, sender_name: currentUser.name, body, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, optimistic]);
    api('/api/team-chat', { method: 'POST', body: JSON.stringify({ body }) })
      .then(r => r.json())
      .then(d => {
        if (d.message) setMessages(prev => prev.map(m => m.id === optimistic.id ? d.message : m));
      })
      .catch(() => setMessages(prev => prev.filter(m => m.id !== optimistic.id)));
  };

  // Format message timestamp
  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div style={{ padding: isMobileLayout ? "1rem" : "1.25rem 1.5rem" }}>
      <style jsx="true">{`
        .calendar-badge, .ical-badge, .outlook-badge {
          font-size: 0.7rem;
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
          color: white;
          font-weight: 500;
        }
        .calendar-badge {
          background-color: #4285f4;
        }
        .ical-badge {
          background-color: #ff6b35;
        }
        .outlook-badge {
          background-color: #0078d4;
        }
        .team-indicator {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          margin-left: 0.5rem;
        }
        .user-avatar {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 1px solid white;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }
      `}</style>

      {showPushPrompt && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '1rem' }}>
          <span style={{ fontSize: '1.25rem' }}>🔔</span>
          <div style={{ flex: 1, fontSize: '0.875rem', color: '#1e3a5f', fontWeight: 500 }}>
            Get notified when someone books with you
          </div>
          <button onClick={handlePushEnable} style={{ background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 7, padding: '0.4rem 0.9rem', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' }}>
            Enable
          </button>
          <button onClick={handlePushDismiss} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, padding: '0 0.25rem', flexShrink: 0 }} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      {!isMobileLayout && (
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Dashboard</h2>
          <HelpButton tourId="dashboard" />
        </div>
      )}

      <OnboardingChecklist onNavigate={setFilter} tasks={tasks} />

      {/* Two-column section */}
      <div
        className="dashboard-columns"
        data-tour-id="dashboard-stats"
        style={{
          marginBottom: "1rem",
          display: "grid",
          gridTemplateColumns: isMobileLayout ? "1fr" : "1fr 1fr",
          gap: "1rem",
        }}
      >

        {/* Left column: Check-in → Pulse → Smart Follow-ups */}
        <div data-tour-id="dashboard-briefing" style={{ display: "flex", flexDirection: "column", gap: "1rem", minWidth: 0 }}>
          <MorningBriefing
            tasks={tasks}
            googleEvents={googleEvents}
            icalEvents={icalEvents}
            outlookEvents={outlookEvents}
          />

          <PulseWidget onViewInsights={isPersonalWorkspace ? null : () => setFilter('Insights')} />

          <FollowUpEngine
            tasks={tasks}
            googleEvents={googleEvents}
            icalEvents={icalEvents}
            outlookEvents={outlookEvents}
            onOpenTask={taskId => setViewTask(tasks.find(t => t.id === taskId) ?? null)}
            onMarkSent={taskId => {
              const t = tasks.find(t => t.id === taskId);
              if (t) handleAideyEditTask({ ...t, completed: true });
            }}
            normalNotifications={normalNotifications}
            onDismissNotification={dismissNotification}
            onNavigate={setFilter}
          />
        </div>

        {/* Right column: Top Tasks → Notes */}
        <div data-tour-id="dashboard-week" style={{ display: "flex", flexDirection: "column", gap: "1rem", minWidth: 0 }}>

          {/* --- Top Tasks box with sort dropdown --- */}
          <div className="dashboard-box dashboard-notes-box">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "0.5rem",
              }}
            >
              <h3 style={{ margin: 0 }}>Top Tasks</h3>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <label htmlFor="dashboardSort" style={{ marginRight: "0.5rem" }}>
                  Sort by:
                </label>
                <select
                  id="dashboardSort"
                  value={dashboardSortBy}
                  onChange={(e) => setDashboardSortBy(e.target.value)}
                  style={{
                    padding: "0.3rem 0.6rem",
                    fontSize: "1rem",
                    borderRadius: "6px",
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="priority">Priority</option>
                  <option value="time">Time</option>
                </select>
              </div>
            </div>
            {topTasks.length === 0 ? (
              <div style={{ padding: "1.25rem", textAlign: "center", color: "#aaa", fontSize: "0.95rem" }}>
                <div style={{ fontSize: "1.5rem", marginBottom: "0.4rem" }}>✅</div>
                No tasks due today — you're all clear!
                <div style={{ marginTop: "0.75rem" }}>
                  <button onClick={() => setFilter("Tasks")} style={{ padding: "0.4rem 1rem", background: "#E4E69B", border: "none", borderRadius: "6px", fontWeight: 600, cursor: "pointer", fontSize: "0.85rem" }}>
                    View all tasks
                  </button>
                </div>
              </div>
            ) : (
              <>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {topTasks.map((task) => (
                    <li
                      key={task.id}
                      className={`task-card ${(task.priority || 'medium').toLowerCase()} dashboard-task ${isGoogleCalendarTask(task) ? 'google-calendar-task' : ''
                        } ${isICalTask(task) ? 'ical-task' : ''
                        } ${isOutlookTask(task) ? 'outlook-task' : ''
                        }`}
                      onClick={() => !isGoogleCalendarTask(task) && !isICalTask(task) && !isOutlookTask(task) && setViewTask(task)}
                      style={{
                        cursor: (isGoogleCalendarTask(task) || isICalTask(task) || isOutlookTask(task)) ? 'default' : 'pointer',
                        opacity: (isGoogleCalendarTask(task) || isICalTask(task) || isOutlookTask(task)) ? 0.9 : 1,
                      }}
                    >
                      <div style={{
                        fontWeight: "600",
                        fontSize: "1rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem"
                      }}>
                        {/* Inline complete button — only for Aidey tasks */}
                        {!isGoogleCalendarTask(task) && !isICalTask(task) && !isOutlookTask(task) && markComplete && (
                          <button
                            onClick={e => { e.stopPropagation(); markComplete(task.id); }}
                            title="Mark complete"
                            className="btn-circle"
                            style={{
                              width: 18, height: 18,
                              borderRadius: "50%",
                              border: "2px solid #aaa", background: "#fff",
                              cursor: "pointer", padding: 0,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: "0.65rem", color: "#aaa",
                            }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = "#27ae60"; e.currentTarget.style.color = "#27ae60"; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = "#aaa"; e.currentTarget.style.color = "#aaa"; }}
                          >
                            ✓
                          </button>
                        )}
                        {isGoogleCalendarTask(task) && (
                          <span className="calendar-badge">
                            📅 Google
                          </span>
                        )}
                        {isICalTask(task) && (
                          <span className="ical-badge">
                            📅 iCal
                          </span>
                        )}
                        {isOutlookTask(task) && (
                          <span className="outlook-badge">
                            🔷 Outlook
                          </span>
                        )}
                        {String(task.title)}

                        {!isGoogleCalendarTask(task) && !isICalTask(task) && !isOutlookTask(task) && task.assignedTeam && task.assignedTeam.length > 1 && (
                          <span
                            title={`Collaborative task with ${task.assignedTeam.length} team members`}
                            style={{
                              marginLeft: "0.5rem",
                              padding: "0.2rem 0.5rem",
                              background: "#e3f2fd",
                              color: "#1565c0",
                              borderRadius: "12px",
                              fontSize: "0.7rem",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px"
                            }}
                          >
                            👥 Team
                          </span>
                        )}
                      </div>
                      {task.taskType && !isGoogleCalendarTask(task) && !isICalTask(task) && !isOutlookTask(task) && (
                        <div style={{ fontSize: "0.85rem", color: "#666" }}>
                          {String(
                            taskTypes.find((t) => t.value === task.taskType)?.label ||
                            ""
                          )}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: "0.85rem",
                          color: "#888",
                          marginTop: "0.25rem",
                        }}
                      >
                        {(isGoogleCalendarTask(task) || isICalTask(task)) ? (
                          task.time || "All day"
                        ) : (
                          <>
                            {new Date(task.datetime).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}{" "}
                            –{" "}
                            {new Date(task.deadline).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}
                          </>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                {allTopTasks.length > 5 && (
                  <button
                    onClick={() => setFilter("Tasks")}
                    style={{
                      marginTop: "0.5rem",
                      background: "#E4E69B",
                      color: "#212121",
                      fontWeight: 600,
                      border: "none",
                      borderRadius: "6px",
                      padding: "0.5rem 1rem",
                      cursor: "pointer",
                      fontSize: "1rem",
                    }}
                  >
                    See More
                  </button>
                )}
              </>
            )}
          </div>

          {/* Follow-up nudges — CRM-framed; business workspace only */}
          {!isPersonalWorkspace && (
            <div data-tour-id="dashboard-followups">
              <FollowUpPanel hideWhenEmpty onNavigate={setFilter} />
            </div>
          )}

          {/* Notes */}
          <div className="dashboard-box">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
              <h3 style={{ margin: 0 }}>Notes</h3>
              {quickNotes.length > 0 && (
                <button
                  onClick={() => setFilter("Notes")}
                  style={{ padding: "0.25rem 0.7rem", background: "#E4E69B", border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: "0.78rem" }}
                >
                  View all →
                </button>
              )}
            </div>

            {quickNotes.length === 0 ? (
              <p style={{ margin: "0 0 1rem", fontSize: "0.9rem", color: "#aaa" }}>No notes yet.</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 1rem" }}>
                {quickNotes.slice(0, 5).map((note) => {
                  const label = note.title?.trim()
                    ? note.title.trim()
                    : note.text?.trim()
                      ? note.text.trim().split(/\s+/).slice(0, 6).join(" ") + (note.text.trim().split(/\s+/).length > 6 ? "…" : "")
                      : "Untitled note";
                  const cat = noteCategories.find(c => c.id === note.categoryId);
                  return (
                    <li
                      key={note.id}
                      onClick={() => setViewNote(note)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "0.75rem",
                        padding: "0.55rem 0",
                        borderBottom: "1px solid #f0f0f0",
                        cursor: "pointer",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", flex: 1, minWidth: 0 }}>
                        {cat && (
                          <span style={{
                            width: 8, height: 8, borderRadius: "50%",
                            background: cat.colour, flexShrink: 0, display: "inline-block",
                          }} />
                        )}
                        <span style={{
                          fontSize: "0.9rem",
                          color: "#0C2D48",
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          {label}
                        </span>
                      </span>
                      {note.created && (
                        <span style={{ fontSize: "0.75rem", color: "#bbb", flexShrink: 0 }}>
                          {formatNoteDate(note.created)}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            <textarea
              placeholder="Write a quick note..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={3}
              style={{
                width: "100%",
                padding: "0.75rem",
                fontSize: "1rem",
                borderRadius: "8px",
                border: "1px solid #ccc",
                marginBottom: "0.5rem",
                boxSizing: "border-box",
                resize: "vertical",
              }}
            />

            {/* Category selector */}
            <select
              value={newNoteCategoryId}
              onChange={(e) => {
                if (e.target.value === '__new__') {
                  setShowDashNewCat(true);
                } else {
                  setNewNoteCategoryId(e.target.value);
                  setShowDashNewCat(false);
                }
              }}
              style={{
                width: "100%",
                padding: "0.5rem 0.75rem",
                fontSize: "0.9rem",
                borderRadius: "8px",
                border: "1px solid #ccc",
                marginBottom: "0.5rem",
                boxSizing: "border-box",
                background: "#fff",
              }}
            >
              <option value="">No category</option>
              {noteCategories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              <option value="__new__">＋ New Category…</option>
            </select>

            {/* Inline new category form */}
            {showDashNewCat && (
              <div style={{
                background: "#f9f9f9", border: "1px solid #e0e0e0",
                borderRadius: 8, padding: "0.75rem", marginBottom: "0.5rem",
              }}>
                <input
                  type="text"
                  placeholder="Category name"
                  value={dashNewCatName}
                  onChange={e => setDashNewCatName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && createDashCategory()}
                  autoFocus
                  style={{
                    width: "100%", padding: "0.4rem 0.6rem",
                    border: "1px solid #ccc", borderRadius: 6,
                    fontSize: "0.9rem", marginBottom: "0.5rem",
                    boxSizing: "border-box",
                  }}
                />
                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
                  {PRESET_COLOURS.map(colour => (
                    <button
                      key={colour}
                      onClick={() => setDashNewCatColour(colour)}
                      className="btn-circle"
                      style={{
                        width: 22, height: 22, borderRadius: "50%",
                        background: colour, border: dashNewCatColour === colour ? "2px solid #333" : "2px solid transparent",
                        cursor: "pointer",
                      }}
                    />
                  ))}
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button
                    onClick={createDashCategory}
                    style={{
                      flex: 1, padding: "0.35rem 0.5rem",
                      background: "#E4E69B", border: "none",
                      borderRadius: 6, fontWeight: 600, cursor: "pointer", fontSize: "0.85rem",
                    }}
                  >
                    Create
                  </button>
                  <button
                    onClick={() => { setShowDashNewCat(false); setDashNewCatName(''); }}
                    style={{
                      padding: "0.35rem 0.75rem", background: "#eee",
                      border: "none", borderRadius: 6, cursor: "pointer", fontSize: "0.85rem",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <button onClick={() => { addNote(newNoteCategoryId); setNewNoteCategoryId(''); }} className="save-note-btn">
              ➕ Save Note
            </button>
          </div>
        </div>
        {/* End right column */}
      </div>
      {/* End two-column section */}

      {/* Team Overview + Leave Requests + Chat — Team Pro / Enterprise / trial only; hidden in personal workspace */}
      {hasTeamAccess && !isPersonalWorkspace && (() => {
        const allTasks = tasks || [];
        const now = new Date();
        const teamStats = (users || []).map(u => {
          const ids = new Set([u.id]);
          const memberTasks = allTasks.filter(t =>
            ids.has(t.userId) || ids.has(t.createdBy) || (Array.isArray(t.assignedTeam) && t.assignedTeam.some(id => ids.has(id)))
          );
          const completed = memberTasks.filter(t => t.completed).length;
          const outstanding = memberTasks.filter(t => !t.completed).length;
          const overdue = memberTasks.filter(t => { const d = t.deadline || t.dueDate; return !t.completed && d && !isNaN(new Date(d)) && new Date(d) < now; }).length;
          return { user: u, total: memberTasks.length, completed, outstanding, overdue };
        }).filter(s => s.total > 0);

        const topCompleter = [...teamStats].sort((a, b) => b.completed - a.completed)[0];
        const mostOutstanding = [...teamStats].sort((a, b) => b.outstanding - a.outstanding)[0];

        return (
          <div className="dashboard-box team-overview-card" style={{ marginBottom: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ margin: 0 }}>Team Overview</h3>
              {isTeamAdmin && (
                <button onClick={() => setFilter("TeamDashboard")} style={{ padding: "0.3rem 0.85rem", background: "#E4E69B", border: "none", borderRadius: "6px", fontWeight: 600, cursor: "pointer", fontSize: "0.82rem" }}>
                  Full dashboard →
                </button>
              )}
            </div>
            {teamStats.length === 0 ? (
              <p style={{ color: "#aaa", margin: 0, fontSize: "0.9rem" }}>No team task data yet.</p>
            ) : (
              <>
                {/* Summary strip */}
                <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                  {[
                    { label: "Total tasks", value: allTasks.length, color: "#0C2D48" },
                    { label: "Completed", value: allTasks.filter(t => t.completed).length, color: "#1e6645" },
                    { label: "Outstanding", value: allTasks.filter(t => !t.completed).length, color: "#7a4a10" },
                    { label: "Overdue", value: allTasks.filter(t => !t.completed && (t.deadline || t.dueDate) && new Date(t.deadline || t.dueDate) < now).length, color: "#A12710" },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ flex: 1, minWidth: 90, padding: "0.6rem 0.75rem", background: "#f8f9fa", border: "1px solid #e5e7eb", borderRadius: 8 }}>
                      <div style={{ fontSize: "0.65rem", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
                      <div style={{ fontSize: "1.25rem", fontWeight: 900, color }}>{value}</div>
                    </div>
                  ))}
                </div>

                {/* Member rows */}
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "0.75rem" }}>
                  {teamStats.map(({ user, total, completed, outstanding, overdue }) => {
                    const pct = total > 0 ? Math.round(completed / total * 100) : 0;
                    return (
                      <div key={user.id} style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: user.color || "#0C2D48", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: "0.72rem", flexShrink: 0 }}>
                          {(user.name || "?")[0].toUpperCase()}
                        </div>
                        <span style={{ minWidth: 70, fontSize: "0.82rem", fontWeight: 600 }}>{user.name}</span>
                        <div style={{ flex: 1, height: 6, background: "#f0f0f0", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, background: pct === 100 ? "#1e6645" : "#E4E69B", borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: "0.75rem", color: "#1e6645", fontWeight: 700, minWidth: 28 }}>✓ {completed}</span>
                        <span style={{ fontSize: "0.75rem", color: "#7a4a10", minWidth: 28 }}>◎ {outstanding}</span>
                        {overdue > 0 && <span style={{ fontSize: "0.72rem", color: "#A12710", fontWeight: 700 }}>⚠ {overdue}</span>}
                      </div>
                    );
                  })}
                </div>

                {/* Callouts */}
                <div style={{ display: "flex", gap: "0.75rem", fontSize: "0.8rem", color: "#555" }}>
                  {topCompleter && <span>🏆 Most completed: <strong>{topCompleter.user.name}</strong> ({topCompleter.completed})</span>}
                  {mostOutstanding && mostOutstanding.outstanding > 0 && (
                    <span>📋 Most work: <strong>{mostOutstanding.user.name}</strong> ({mostOutstanding.outstanding} open)</span>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Family Chat widget — personal workspace only, self-hides when < 2 participants */}
      {isPersonalWorkspace && <FamilyChatWidget onOpen={() => setFilter('FamilyChat')} />}

      {/* Leave Requests — team plan only, component self-hides for non-managers; hidden in personal workspace */}
      {hasTeamAccess && !isPersonalWorkspace && <LeaveRequests />}

      {/* Team Chat — team plan only; hidden in personal workspace */}
      {hasTeamAccess && !isPersonalWorkspace && <div className="dashboard-box" style={{ marginTop: "1rem", marginBottom: 0 }}>
        <h3>Team Chat</h3>

        {/* Team Member Tabs */}
        <div style={{
          display: "flex",
          gap: "0.5rem",
          marginBottom: "1rem",
          overflowX: "auto",
          paddingBottom: "0.25rem"
        }}>
          {/* All Team tab */}
          <button
            onClick={() => setActiveTab("all")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.5rem 1rem",
              backgroundColor: activeTab === "all" ? "#e4e69b" : "#f3f3f3",
              color: activeTab === "all" ? "#212121" : "#495057",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "0.9rem",
              fontWeight: "600",
              whiteSpace: "nowrap",
              minWidth: "fit-content"
            }}
          >
            👥 All Team
          </button>

          {users
            .filter(u => u.id !== currentUser?.id)
            .map(user => (
              <button
                key={user.id}
                onClick={() => setActiveTab(user.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.5rem 1rem",
                  backgroundColor: activeTab === user.id ? "#e4e69b" : "#f3f3f3",
                  color: activeTab === user.id ? "#212121" : "#495057",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  whiteSpace: "nowrap",
                  minWidth: "fit-content"
                }}
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor: user.color || "#007acc",
                    flexShrink: 0
                  }}
                />
                {user.name}
              </button>
            ))}
        </div>

        {users.filter(u => u.id !== currentUser?.id).length === 0 ? (
          <div style={{
            padding: "3rem",
            backgroundColor: "#f8f9fa",
            borderRadius: "12px",
            textAlign: "center",
            border: "2px dashed #dee2e6"
          }}>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>👥</div>
            <div style={{ fontSize: "1.2rem", fontWeight: "600", color: "#495057", marginBottom: "0.5rem" }}>
              No team members yet
            </div>
            <div style={{ color: "#6c757d", fontSize: "1rem", marginBottom: "1rem" }}>
              Invite your first team member to get started
            </div>
            <button
              onClick={() => setFilter("TeamManagement")}
              style={{ padding: "0.5rem 1.25rem", background: "#E4E69B", border: "none", borderRadius: "6px", fontWeight: 600, cursor: "pointer", fontSize: "0.9rem" }}
            >
              Invite Team Members
            </button>
          </div>
        ) : activeTab ? (
          <>
            {/* Chat Messages Area */}
            <div style={{
              maxHeight: "300px",
              overflowY: "auto",
              backgroundColor: "#fff",
              padding: "1rem",
              borderRadius: "8px",
              border: "1px solid #dee2e6",
              marginBottom: "1rem",
              minHeight: "200px"
            }}>
              {(() => { const visible = activeTab === 'all' ? messages : messages.filter(m => m.user_id === activeTab); return visible.length === 0 ? (
                <div style={{
                  textAlign: "center",
                  color: "#6c757d",
                  padding: "2rem",
                  fontSize: "1rem"
                }}>
                  {activeTab === "all"
                    ? "💬 No messages yet. Say something to the whole team!"
                    : `💬 No messages yet. Start the conversation with ${(users || []).find(u => u.id === activeTab)?.name || 'your teammate'}!`}
                </div>
              ) : (
                visible.map((msg, idx) => (
                  <div
                    key={msg.id}
                    style={{
                      marginBottom: "0.75rem",
                      padding: "0.5rem 0",
                      borderBottom: idx < visible.length - 1 ? "1px solid #f1f3f4" : "none"
                    }}
                  >
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginBottom: "0.25rem"
                    }}>
                      <strong style={{ color: "#495057" }}>{msg.sender_name}:</strong>
                      {msg.created_at && (
                        <span style={{
                          fontSize: "0.75rem",
                          color: "#6c757d"
                        }}>
                          {formatMessageTime(msg.created_at)}
                        </span>
                      )}
                    </div>
                    <div style={{
                      color: "#333",
                      lineHeight: "1.4"
                    }}>
                      {msg.body}
                    </div>
                  </div>
                ))
              ); })()}
            </div>

            {/* Message Input */}
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSend();
                  }
                }}
                placeholder={activeTab === "all" ? "Send a message to the whole team..." : `Type a message to ${users.find(u => u.id === activeTab)?.name}...`}
                style={{
                  flex: 1,
                  padding: "0.75rem",
                  borderRadius: "6px",
                  border: "1px solid #ced4da",
                  fontSize: "0.95rem",
                  outline: "none"
                }}
                onFocus={(e) => e.target.style.borderColor = "#007acc"}
                onBlur={(e) => e.target.style.borderColor = "#ced4da"}
              />
              <button
                onClick={handleSend}
                disabled={!newMessage.trim()}
                style={{
                  padding: "0.75rem 1.5rem",
                  backgroundColor: newMessage.trim() ? "#82b3cc" : "#dee2e6",
                  color: newMessage.trim() ? "#fff" : "#6c757d",
                  border: "none",
                  borderRadius: "6px",
                  cursor: newMessage.trim() ? "pointer" : "not-allowed",
                  fontSize: "0.95rem",
                  fontWeight: "600"
                }}
              >
                Send
              </button>
            </div>

            <div style={{
              fontSize: "0.75rem",
              color: "#6c757d",
              textAlign: "center",
              marginTop: "0.5rem"
            }}>
              Press Enter to send
            </div>
          </>
        ) : (
          <div style={{
            padding: "2rem",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            textAlign: "center",
            color: "#6c757d"
          }}>
            Select a team member to start chatting
          </div>
        )}
      </div>}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="scroll-up-btn"
          style={{
            position: 'fixed',
            bottom: '140px',
            right: '2rem',
            backgroundColor: '#3498db', // ✅ Nice blue color
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '48px',
            height: '48px',
            fontSize: '1.5rem',
            cursor: 'pointer',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            zIndex: 1500,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#2980b9';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#3498db';
            e.target.style.transform = 'scale(1)';
          }}
          title="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  );
}

export default Dashboard;